import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { requireToken, SessionExpiredError } from "../auth/authService";

import { configs } from "../configurations";
import useForm from "../useForm";
import validateMerchantInvite from "./merchantInviteValidationRules";
import { getFromMCAPI } from "./merchantConfigurationApi";
import { PageHeader } from "./PageHeader";
import { paths } from "../routes";

export function InviteMerchant() {
  const { email } = useAuth();
  const [envConfigs, setEnvConfigs] = useState({});
  const [partners, setPartners] = useState([]);
  const [partnerError, setPartnerError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const getPartnerList = (configs, token) => {
    var endpoint =
      configs.merchantConfigurationBaseUri + configs.partnersEndpoint;
    getFromMCAPI(token, endpoint)
      .then((response) => {
        //  Defensive even now that the wrapper throws: partners.map() is in the render path,
        //  so anything falsy here white-screens the page rather than showing a message.
        setPartners(Array.isArray(response) ? response : []);
        setPartnerError(null);
      })
      .catch((error) => {
        console.error("Failed to load the partner list:", error);
        setPartners([]);
        setPartnerError(
          "Could not load the partner list. You may not have access, or the merchant configuration API may be unreachable.",
        );
      });
  };

  // Functional component equivalent of componentDidMount()
  useEffect(() => {
    let mounted = true;

    const acquireAccessToken = (configurations) => {
      let token;
      try {
        token = requireToken();
      } catch (error) {
        //  requireToken is already navigating to the login screen.
        if (!(error instanceof SessionExpiredError)) console.error(error);
        return;
      }

      //  Not stashed in state: the submit path re-reads it, because a form left open can
      //  outlive the session.
      if (mounted) getPartnerList(configurations, token);
    };

    configs().then((items) => {
      if (mounted) {
        setEnvConfigs(items);
        acquireAccessToken(items);
      }
    });
    return () => (mounted = false);
  }, []);

  const { values, errors, handleChange, handleSubmit } = useForm(
    createAndSendMerchantInvite,
    validateMerchantInvite,
  );

  async function createAndSendMerchantInvite() {
    if (values) {
      //  Before setIsSubmitting, deliberately. The token in state was read when the page
      //  loaded, and platform-services sessions are often only a couple of hours, so a form
      //  left open can easily outlive it. Checking here means an expired session redirects
      //  to the login screen instead of posting a dead token and 401ing, and doing it first
      //  means the button never sticks in its spinner while the page navigates away.
      let currentToken;
      try {
        currentToken = requireToken();
      } catch (tokenError) {
        if (!(tokenError instanceof SessionExpiredError))
          console.error(tokenError);
        return;
      }

      setIsSubmitting(true); // This still sets component-level isSubmitting state for UI updates

      try {
        const headers = {
          Accept: "application/json",
          "Content-Type": "application/json",
          Pragma: "no-cache",
        };
        const headersWithAuth = JSON.parse(JSON.stringify(headers));
        headersWithAuth["Authorization"] = "Bearer " + currentToken;

        let fetchOptions = {
          method: "POST",
          headers: headersWithAuth,
        };

        //  First, we try to create a MerchantInvite
        const createInviteEndpoint =
          envConfigs.merchantConfigurationBaseUri +
          envConfigs.merchantInvitesEndpoint;

        fetchOptions["body"] = JSON.stringify({
          sentBy: email,
          emailAddress: values.inviteeEmail,
          country: values.country,
          partnerId: values.partner,
        });

        let merchantInvite = await fetch(
          createInviteEndpoint,
          fetchOptions,
        ).then((response) => response.json());

        console.log(merchantInvite);
        navigate(paths.inviteHistory);
      } catch (error) {
        console.error("Failed to create merchant invite:", error);
        // You could add an error state here and display it to the user
      } finally {
        setIsSubmitting(false);
      }
    }
  }

  return (
    <div>
      <PageHeader
        title="Invite Merchant"
        lead="Send an onboarding invitation to a merchant's controller or owner."
      />
      {partnerError && (
        <div className="alert alert-danger" role="alert">
          {partnerError}
        </div>
      )}
      {/*  Constrained now that the page container is wide: a form whose inputs run to
           1600px is unusable. Mirrors how Login.js sizes itself. */}
      <div className="row">
        <div className="col-12 col-md-8 col-lg-6">
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
              <label htmlFor="inviteeEmail" className="form-label">
                Email{" "}
                <span className="form-text">
                  (The merchant Controller or Owner completing the application.)
                </span>
              </label>
              <input
                autoComplete="off"
                className={`form-control ${errors.inviteeEmail && "is-invalid"}`}
                name="inviteeEmail"
                onChange={handleChange}
                value={values.inviteeEmail || ""}
              />
              {errors.inviteeEmail && (
                <div className="text-danger">{errors.inviteeEmail}</div>
              )}
            </div>
            <div className="mb-3">
              <label htmlFor="partner" className="form-label">
                Partner{" "}
                <span className="form-text">
                  (The TowPay Partner for this merchant.)
                </span>
              </label>
              <select
                autoComplete="off"
                className={`form-select ${errors.partner && "is-invalid"}`}
                name="partner"
                onChange={handleChange}
                defaultValue={values.partner || ""}
              >
                <option disabled value="">
                  Choose...
                </option>
                {partners.map((partner) => (
                  <option key={partner.partnerId} value={partner.partnerId}>
                    {partner.name}
                  </option>
                ))}
              </select>
              {errors.partner && (
                <div className="text-danger">{errors.partner}</div>
              )}
            </div>
            <div className="mb-3">
              <label htmlFor="country" className="form-label">
                Country:
              </label>
              <select
                autoComplete="off"
                className={`form-select ${errors.country && "is-invalid"}`}
                name="country"
                onChange={handleChange}
                defaultValue={values.country || ""}
              >
                <option disabled value="">
                  Choose...
                </option>
                <option key="us" value="us">
                  United States
                </option>
                <option key="ca" value="ca">
                  Canada
                </option>
              </select>
              {errors.country && (
                <div className="text-danger">{errors.country}</div>
              )}
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Sending Invitation...
                </>
              ) : (
                "Invite"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
